import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { Subcontractor, SubcontractorUser } from '../types';

const userName = (u: SubcontractorUser) => `${u.first_name} ${u.last_name}`;

interface UseSubcontractorsReturn {
  subcontractors: Subcontractor[];
  addSubcontractor: (name: string) => Promise<Subcontractor | null>;
  addSubcontractorUser: (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ) => Promise<SubcontractorUser | null>;
}

export function useSubcontractors(orgId: string | null): UseSubcontractorsReturn {
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);

  useEffect(() => {
    if (!orgId) { setSubcontractors([]); return; }
    supabase
      .from('subcontractors')
      .select('*, subcontractor_users(*)')
      .eq('organization_id', orgId)
      .eq('archived', false)
      .order('name')
      .then(({ data }) =>
        setSubcontractors(
          (data ?? []).map((s) => ({
            ...s,
            subcontractor_users: sortByName(s.subcontractor_users ?? [], userName),
          })),
        ),
      )
      .catch(() => setSubcontractors([]));
  }, [orgId]);

  const addSubcontractor = async (name: string): Promise<Subcontractor | null> => {
    if (!orgId) return null;
    try {
      const { data, error } = await supabase
        .from('subcontractors')
        .insert({ name, organization_id: orgId, archived: false })
        .select()
        .single();
      if (error) throw error;
      const created = { ...(data as Subcontractor), subcontractor_users: [] };
      setSubcontractors((prev) => sortByName([...prev, created]));
      return created;
    } catch {
      return null;
    }
  };

  const addSubcontractorUser = async (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ): Promise<SubcontractorUser | null> => {
    try {
      const existing = subcontractors.find((s) => s.id === subcontractorId)?.subcontractor_users ?? [];
      const isDefault = existing.length === 0;
      const { data, error } = await supabase
        .from('subcontractor_users')
        .insert({
          subcontractor_id: subcontractorId,
          first_name: firstName,
          last_name: lastName,
          is_default: isDefault,
        })
        .select()
        .single();
      if (error) throw error;
      const created = data as SubcontractorUser;
      setSubcontractors((prev) =>
        prev.map((s) =>
          s.id === subcontractorId
            ? { ...s, subcontractor_users: sortByName([...(s.subcontractor_users ?? []), created], userName) }
            : s,
        ),
      );
      return created;
    } catch {
      return null;
    }
  };

  return { subcontractors, addSubcontractor, addSubcontractorUser };
}
